import React, { useState, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, Ticket, Plus, FileText, LogOut, Menu, X, Shield, KanbanSquare, Package, CheckSquare, FileInput, FolderOpen, FileSignature, MessageCircle, Phone, Truck, PhoneCall, Bot, MessageSquare, PhoneOutgoing, Bell, BellRing, Briefcase, Receipt, ChevronDown, ChevronRight, CalendarDays, Trash2, Settings as SettingsIcon, FileCheck
} from 'lucide-react';
import MinervaChatModal from './MinervaChatModal';
import IdleLogoutMonitor from './IdleLogoutMonitor';

const OPEN_GROUPS_KEY = 'crmllor_sidebar_open_groups';

const Layout = () => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Estructura del menú con grupos desplegables.
  // type: 'link' (NavLink directo) | 'group' (cabecera plegable con children)
  const allNavItems = [
    { type: 'link', to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true, module: 'dashboard' },
    { type: 'link', to: '/phonebook', icon: Phone, label: 'Agenda', module: 'phonebook' },
    { type: 'link', to: '/conversations', icon: MessageSquare, label: 'Conversaciones', module: 'phonebook' },
    { type: 'link', to: '/outbound-calls', icon: PhoneOutgoing, label: 'Llamadas salientes', module: 'phonebook' },
    { type: 'link', to: '/reminders', icon: Bell, label: 'Recordatorios', module: 'phonebook' },
    { type: 'link', to: '/calendar', icon: CalendarDays, label: 'Calendario', module: 'phonebook' },
    {
      type: 'group',
      key: 'tickets',
      icon: Ticket,
      label: 'Tickets',
      children: [
        { to: '/tickets/new', icon: Plus, label: 'Nuevo ticket', module: 'tickets' },
        { to: '/tickets', icon: Ticket, label: 'Ver tickets', module: 'tickets', end: true },
        { to: '/tickets/kanban', icon: KanbanSquare, label: 'Kanban', module: 'tickets' },
        { to: '/tickets/avisos', icon: BellRing, label: 'Avisos', module: 'tickets' },
      ],
    },
    { type: 'link', to: '/activities', icon: CheckSquare, label: 'Actividades', module: 'activities' },
    {
      type: 'group',
      key: 'comercial',
      icon: Briefcase,
      label: 'Comercial',
      children: [
        { to: '/customers', icon: Users, label: 'Clientes', module: 'customers' },
        { to: '/providers', icon: Truck, label: 'Proveedores', module: 'providers' },
        { to: '/products', icon: Package, label: 'Productos', module: 'products' },
        { to: '/contracts', icon: FileSignature, label: 'Contratos', module: 'contracts' },
        { to: '/budgets', icon: FolderOpen, label: 'Presupuestos', module: 'invoices' },
      ],
    },
    {
      type: 'group',
      key: 'facturas',
      icon: Receipt,
      label: 'Facturas',
      children: [
        { to: '/invoices', icon: FileText, label: 'Salientes', module: 'invoices' },
        { to: '/proformas', icon: FileSignature, label: 'Proformas', module: 'invoices' },
        { to: '/received-invoices', icon: FileInput, label: 'Entrantes', module: 'received_invoices' },
        { to: '/received-invoices/quarter-review', icon: CalendarDays, label: 'Repaso trimestral', module: 'received_invoices' },
      ],
    },
    {
      type: 'group',
      key: 'administracion',
      icon: Shield,
      label: 'Administración',
      children: [
        { to: '/call-cases', icon: PhoneCall, label: 'Casos de llamada', module: 'phonebook' },
        { to: '/minerva', icon: Bot, label: 'Configuración Minerva', module: 'phonebook' },
        { to: '/papelera', icon: Trash2, label: 'Papelera' },
        { to: '/declaracion-responsable', icon: FileCheck, label: 'Declaración responsable' },
        { to: '/admin', icon: Shield, label: 'Panel admin', requiresSuperAdmin: true },
      ],
    },
  ];

  // Filtra items según permisos. Un grupo se muestra si al menos un hijo pasa el filtro.
  const navItems = useMemo(() => {
    const passes = (it) => {
      if (it.requiresSuperAdmin) return !!user?.isSuperAdmin;
      if (!it.module) return true;
      return hasPermission(it.module, 'view');
    };
    return allNavItems
      .map((item) => {
        if (item.type === 'link') {
          return passes(item) ? item : null;
        }
        if (item.type === 'group') {
          const children = item.children.filter(passes);
          return children.length > 0 ? { ...item, children } : null;
        }
        return null;
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasPermission]);

  // Estado de apertura de grupos: arranca abierto el grupo cuya ruta activa coincida con un hijo,
  // y respeta lo que el usuario haya plegado/desplegado manualmente (persistido en localStorage).
  const [openGroups, setOpenGroups] = useState(() => {
    let saved = {};
    try {
      const raw = localStorage.getItem(OPEN_GROUPS_KEY);
      if (raw) saved = JSON.parse(raw) || {};
    } catch (e) {
      saved = {};
    }
    const initial = { ...saved };
    navItems.forEach((item) => {
      if (item.type === 'group') {
        const isChildActive = item.children.some((c) =>
          c.end ? location.pathname === c.to : location.pathname.startsWith(c.to)
        );
        if (isChildActive) initial[item.key] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
      } catch (e) {
        // localStorage saturado: ignoramos
      }
      return next;
    });
  };

  const renderLink = (item, opts = {}) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? 'active' : ''} ${opts.child ? 'sidebar-link--child' : ''}`
      }
      onClick={() => setSidebarOpen(false)}
    >
      <item.icon />
      {item.label}
    </NavLink>
  );

  return (
    <div className="app-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <h1>CRM<span>llor</span></h1>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            if (item.type === 'link') return renderLink(item);
            const isOpen = !!openGroups[item.key];
            const Chevron = isOpen ? ChevronDown : ChevronRight;
            const anyChildActive = item.children.some((c) =>
              c.end ? location.pathname === c.to : location.pathname.startsWith(c.to)
            );
            return (
              <div key={item.key} className="sidebar-group">
                <button
                  type="button"
                  className={`sidebar-link sidebar-group-toggle ${anyChildActive ? 'has-active-child' : ''}`}
                  onClick={() => toggleGroup(item.key)}
                  aria-expanded={isOpen}
                >
                  <item.icon />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <Chevron className="sidebar-chevron" />
                </button>
                {isOpen && (
                  <div className="sidebar-group-children">
                    {item.children.map((child) => renderLink(child, { child: true }))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-link"
            onClick={() => setChatOpen(true)}
            style={{ color: '#a7f3d0' }}
          >
            <MessageCircle />
            Chat con Minerva
          </button>
          <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: '#94a3b8', marginBottom: 8 }}>
            {user?.name}
          </div>
          <NavLink
            to="/settings"
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <SettingsIcon />
            Mis ajustes
          </NavLink>
          <button className="sidebar-link" onClick={handleLogout}>
            <LogOut />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <button
        className="btn-icon"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          position: 'fixed', top: 12, left: 12, zIndex: 150,
          display: 'none',
          background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', borderRadius: 8
        }}
        id="mobile-menu-btn"
      >
        {sidebarOpen ? <X /> : <Menu />}
      </button>

      {/* key=pathname: remonta la página en cada cambio de ruta para que sus
          datos se recarguen al abrir (cubre también /invoices <-> /proformas,
          que son el mismo componente y antes no se refrescaban). Solo remonta
          la página; el menú, la cabecera y el FAB quedan fuera de <main>. */}
      <main className="main-content" key={location.pathname}>
        <Outlet />
      </main>

      <MinervaChatModal open={chatOpen} onClose={() => setChatOpen(false)} />

      <IdleLogoutMonitor />

      <style>{`
        @media (max-width: 1024px) {
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </div>
  );
};

export default Layout;
